/**
 * §P.5 · El pasaporte de grados de un alumno.
 *
 * ## Va en la ficha y no en una pantalla aparte
 *
 * «Qué cinturón tiene» es de las tres cosas que se miran de un alumno, junto con si
 * paga y en qué grupo está. Mandarlo a otra pantalla convertiría una mirada en dos
 * clics.
 *
 * ## Otorgar a mano es el caso normal, no la excepción
 *
 * Los exámenes son fase 2, pero **otorgar sin examen hace falta desde el primer
 * día**: el alumno que llega de otro club con su cinturón puesto, y el club que
 * está cargando quince años de histórico en papel. Por eso la fecha se acepta en el
 * pasado —y solo en el pasado: una fecha futura descuadraría los meses hasta el
 * siguiente examen y se lo adelantaría a esa persona—.
 *
 * ## Las franjas van con su cinturón
 *
 * «Tres franjas» es una cifra; «tres franjas de verde» es un sitio en la escalera.
 * Por eso el servidor manda grados y franjas juntos y aquí se pintan sobre la misma
 * muestra de color.
 *
 * ## El grado lo puede dar el entrenador
 *
 * `POST /students/{id}/ranks` acepta `coa`, a diferencia de casi todo §P.3 y §P.4.
 * Tiene sentido: quien decide que alguien está listo para el amarillo es quien le
 * da clase. Lo que el entrenador no toca es el dinero.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { fecha } from '@/shared/ui/formato';
import { Cinturon } from '@/club/grados/Cinturon';
import {
  fechaDeGradoValida,
  hoyComoDia,
  nombreDeCinturon,
  type Cinturon as TipoDeCinturon,
  type Pasaporte,
  type SistemaDeGrados,
} from '@/club/grados/tipos';

type Haciendo = 'nada' | 'otorgar';

export function PasaporteDelAlumno({
  club,
  alumnoId,
  puedeEditar,
}: {
  club: string;
  alumnoId: string;
  puedeEditar: boolean;
}) {
  const cache = useQueryClient();
  const [haciendo, setHaciendo] = useState<Haciendo>('nada');
  const [cinturonId, setCinturonId] = useState('');
  const [cuando, setCuando] = useState(hoyComoDia);
  const [certificado, setCertificado] = useState('');
  const [nota, setNota] = useState('');
  const [problema, setProblema] = useState<Problema | null>(null);

  const pasaporte = useQuery({
    queryKey: ['pasaporte', club, alumnoId],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        .ranks.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Pasaporte;
    },
  });

  /*
   * Los cinturones que se pueden otorgar. Se piden solo al abrir el formulario: en
   * la vista de lectura no hacen falta y son una consulta por alumno abierto.
   */
  const sistemas = useQuery({
    queryKey: ['grados', club, 'en-uso'],
    enabled: haciendo === 'otorgar',
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club })['belt-schemes'].get({
        /*
         * Con las plantillas: un club puede usar la plantilla global tal cual
         * —Ajustes → Disciplinas lo ofrece así a uno recién llegado— y sin ellas
         * esa escalera no aparecía. El club no podía otorgar un grado desde la
         * ficha ni avisar por cinturón. 24-09-2026.
         */
        query: { include_templates: true },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return (data as unknown as SistemaDeGrados[]).filter((s) => s.in_use);
    },
  });

  const enUso = sistemas.data?.[0];

  const cinturones = useQuery({
    queryKey: ['cinturones', club, enUso?.id],
    enabled: Boolean(enUso),
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        ['belt-schemes']({ schemeId: enUso!.id })
        .ranks.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as TipoDeCinturon[];
    },
  });

  const refrescar = () => {
    void cache.invalidateQueries({ queryKey: ['pasaporte', club, alumnoId] });
    // El contador de «cuántos lo llevan» de la escalera cambia con esto.
    void cache.invalidateQueries({ queryKey: ['cinturones', club] });
  };

  const otorgar = useMutation({
    mutationFn: async () => {
      if (!fechaDeGradoValida(cuando)) {
        throw new ErrorDeApi({
          title: 'Esa fecha no vale',
          status: 400,
          detail:
            'Un grado se puede otorgar con fecha pasada —para cargar el histórico— pero no en el futuro: descuadraría los meses hasta el siguiente examen.',
          errors: [{ field: 'awarded_on', message: 'No puede ser futura' }],
        });
      }

      const r = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        .ranks.post(
          {
            belt_rank_id: cinturonId,
            awarded_on: cuando,
            certificate_ref:
              certificado.trim() === '' ? null : certificado.trim(),
            note: nota.trim() === '' ? null : nota.trim(),
          },
          { headers: { 'idempotency-key': crypto.randomUUID() } }
        );
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setHaciendo('nada');
      setCinturonId('');
      setCertificado('');
      setNota('');
      setCuando(hoyComoDia());
      refrescar();
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const ponerFranja = useMutation({
    mutationFn: async () => {
      const r = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        // Sin `stripe_no`: la siguiente que toque, que es lo que se pulsa casi
        // siempre. Teclear el número es una forma de equivocarse sin necesidad.
        .stripes.post({});
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: refrescar,
    onError: (e) => setProblema(aProblema(e)),
  });

  if (pasaporte.isPending) {
    return <Cargando filas={2} etiqueta="Cargando los grados" />;
  }

  if (pasaporte.isError || !pasaporte.data) {
    return (
      <PanelError
        problema={aProblema(pasaporte.error)}
        reintentar={() => void pasaporte.refetch()}
      />
    );
  }

  const p = pasaporte.data;
  const actual = p.ranks.find((r) => r.is_current) ?? null;
  const anteriores = p.ranks.filter((r) => !r.is_current);

  // ── Otorgar un grado ─────────────────────────────────────────────────────
  if (haciendo === 'otorgar') {
    return (
      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          otorgar.mutate();
        }}
      >
        <h3>Otorgar un grado</h3>

        {/* `isLoading`: ver `FormularioDeAviso`. Sin sistema en uso, la consulta
            de cinturones no se lanza y con `isPending` cargaba para siempre. */}
        {(sistemas.isLoading || cinturones.isLoading) && (
          <Cargando filas={1} etiqueta="Cargando" />
        )}

        {sistemas.data && !enUso && (
          <div className="nota">
            El club no tiene ningún sistema de grados en uso. Se elige en{' '}
            <strong>Grados</strong>, y hasta entonces no hay escalera de la que
            otorgar.
          </div>
        )}

        {cinturones.data && (
          <div className="rejilla">
            <label>
              Cinturón
              <select
                required
                value={cinturonId}
                onChange={(e) => setCinturonId(e.target.value)}
              >
                <option value="">Elige un cinturón</option>
                {cinturones.data.map((c) => (
                  <option key={c.id} value={c.id}>
                    {nombreDeCinturon(c)}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Cuándo
              {/*
                `max` de hoy: el navegador impide el futuro antes de que lo haga el
                servidor. La comprobación de verdad sigue estando en los dos sitios.
              */}
              <input
                type="date"
                required
                max={hoyComoDia()}
                value={cuando}
                onChange={(e) => setCuando(e.target.value)}
              />
            </label>

            <label>
              Nº de certificado
              <input
                maxLength={60}
                placeholder="El federativo, si lo hay"
                value={certificado}
                onChange={(e) => setCertificado(e.target.value)}
              />
            </label>

            <label>
              Nota
              <input
                maxLength={500}
                placeholder="Llega de otro club, examen de la federación…"
                value={nota}
                onChange={(e) => setNota(e.target.value)}
              />
            </label>
          </div>
        )}

        <div className="nota">
          <strong>La fecha puede ser pasada</strong>, que es de lo que va cargar
          un histórico de papel. Futura no: le adelantaría el siguiente examen a
          esta persona.
        </div>

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button
            type="submit"
            disabled={otorgar.isPending || !cinturonId || !enUso}
          >
            {otorgar.isPending ? 'Otorgando…' : 'Otorgar el grado'}
          </button>
          <button
            type="button"
            className="btn-plano"
            onClick={() => {
              setHaciendo('nada');
              setProblema(null);
            }}
          >
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  // ── El pasaporte ─────────────────────────────────────────────────────────
  return (
    <div className="pasaporte">
      {actual ? (
        <div className="grado-actual">
          <Cinturon
            color={actual.color_hex}
            color2={actual.color_hex_2}
            nombre={nombreDeCinturon({
              name: actual.rank_name,
              short_name: actual.short_name,
            })}
            franjas={p.current_stripes}
            deDan={actual.is_dan}
          />
          <span className="tenue">desde el {fecha(actual.awarded_on)}</span>
        </div>
      ) : (
        <p className="tenue">
          Todavía no tiene grado.{' '}
          {puedeEditar && 'Se le puede otorgar el primero sin examen.'}
        </p>
      )}

      {/*
        El histórico plegado: lo de hoy es lo que se mira, y lo de 2019 es lo que se
        consulta cuando alguien pregunta. Pero está, porque es el pasaporte.
      */}
      {anteriores.length > 0 && (
        <details className="grados-pasados">
          <summary>
            {anteriores.length === 1
              ? 'Tuvo 1 grado antes'
              : `Tuvo ${anteriores.length} grados antes`}
          </summary>
          <ul className="grados-lista">
            {anteriores.map((g) => (
              <li key={g.id}>
                <Cinturon
                  color={g.color_hex}
                  color2={g.color_hex_2}
                  nombre={nombreDeCinturon({
                    name: g.rank_name,
                    short_name: g.short_name,
                  })}
                  deDan={g.is_dan}
                />
                <span className="tenue">
                  {fecha(g.awarded_on)}
                  {/* De dónde salió: un examen o la mano de alguien. */}
                  {g.exam_name ? ` · ${g.exam_name}` : ' · a mano'}
                  {g.certificate_ref ? ` · nº ${g.certificate_ref}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {problema && <PanelError problema={problema} />}

      {puedeEditar && (
        <div className="acciones">
          <button
            type="button"
            className="btn-plano"
            onClick={() => {
              setHaciendo('otorgar');
              setProblema(null);
            }}
          >
            Otorgar un grado
          </button>

          {actual && (
            <button
              type="button"
              className="btn-plano"
              disabled={ponerFranja.isPending}
              onClick={() => {
                setProblema(null);
                ponerFranja.mutate();
              }}
            >
              {ponerFranja.isPending ? 'Poniendo…' : 'Poner una franja'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
